import saveAs from 'file-saver';
import axios from 'axios';
import get from 'lodash.get';
import { Parser } from 'm3u8-parser';
import { getAxiosError, denormalizeContextFilters } from 'components/utils/helpers';
import { ASSET_TYPES } from 'components/utils/constants';
import {
  SET_VIDEO_ERROR,
  SET_VIDEO_LOADING,
  SET_CLIP,
  SET_VIDEO_DOWNLOADING,
  SET_VIDEO_PERMISSIONS,
  SET_USER_HAS_REMAINING_VIDEO_QUOTA,
} from '../reducers/videos';
import {
  showQuotaExceededModal,
} from './globalActions';
import { setFilterGroup, updateFilter } from './search';

export const VIDEO_TIMEOUT = process.env.VIDEO_TIMEOUT || 10000;
export const VIDEO_CAPTION_TIMEOUT = process.env.VIDEO_CAPTION_TIMEOUT || 5000;

export const setVideoError = error => ({
  type: SET_VIDEO_ERROR,
  data: { error },
});

export const setVideoLoading = (loading = true) => ({
  type: SET_VIDEO_LOADING,
  data: { loading },
});

export const setVideoDownloading = (downloading = true) => ({
  type: SET_VIDEO_DOWNLOADING,
  data: { downloading },
});

export const setClip = clip => ({
  type: SET_CLIP,
  data: { clip },
});

export const setPermissions = permissions => ({
  type: SET_VIDEO_PERMISSIONS,
  data: { permissions },
});

export const setUserHasRemainingQuota = (hasRemainingQuota = true) => ({
  type: SET_USER_HAS_REMAINING_VIDEO_QUOTA,
  data: { hasRemainingQuota },
});

const handleError = (dispatch, err) => {
  const { status, message } = getAxiosError(err);
  const errorMsg = status === 404 ? status : message;
  dispatch(setVideoError(errorMsg));
};

export const getPlaylistData = async (url) => {
  if (!url) return {};

  try {
    const parser = new Parser();
    const playlistResponse = await axios.get(url, { timeout: VIDEO_CAPTION_TIMEOUT });
    parser.push(playlistResponse.data);
    parser.end();
    return parser.manifest;
  } catch (err) {
    console.warn('Error retrieving video closed-caption data');
    return {};
  }
};

export const getVideoJsCaption = (playlistUrl, label) => async () => {
  if (!playlistUrl) return {};

  try {
    const captionBaseUrl = new URL(playlistUrl).origin;
    const playlistData = await getPlaylistData(playlistUrl);
    const captionPlaylistUrl = get(playlistData, `mediaGroups.SUBTITLES.subs.${label}.uri`, false);
    const captionPlaylistData = await getPlaylistData(captionPlaylistUrl);
    const captionPath = get(captionPlaylistData, 'segments[0].uri', '');

    return {
      label,
      kind: 'captions',
      language: get(playlistData, `mediaGroups.SUBTITLES.subs.${label}.language`, ''),
      src: captionPath ? (captionBaseUrl + captionPath) : '',
    };
  } catch (err) {
    console.warn('Error assembling video closed-caption data');
    return {};
  }
};

export const defaultVideoFilters = () => async (dispatch, getState) => {
  const { filters: { brands } } = getState().search;
  const { user: { videoBrands } } = getState().user;
  if ((!brands || !Object.keys(brands).length) && videoBrands && videoBrands.length) {
    const { brands: denormalizedBrands } = denormalizeContextFilters({ brands: videoBrands.map(({ refName, name }) => ({ value: refName, displayValue: name })) });
    dispatch(setFilterGroup('brands', denormalizedBrands));
    videoBrands.forEach(videoBrand => dispatch(updateFilter('video', videoBrand.refName, true)));
  }
};

export const getVideo = id => async (dispatch) => {
  if (!id) return;

  try {
    let playerCaptions = {};
    dispatch(setClip({}));
    dispatch(setVideoLoading());
    const { data } = await axios.get(`/api/videos/${id}`, { timeout: VIDEO_TIMEOUT });

    if (data.subtitles && data.subtitles.url) {
      playerCaptions = await dispatch(getVideoJsCaption(data.subtitles.url, 'English'));
    }

    dispatch(setClip({ ...data, playerCaptions }));
  } catch (err) {
    dispatch(handleError(dispatch, err));
  } finally {
    dispatch(setVideoLoading(false));
  }
};

export const getVideoPermissions = id => async (dispatch) => {
  if (!id) return;

  let newPermissions = { renditions: {}, permissions: {} };
  try {
    const { data } = await axios.get(`/api/videos/${id}/permissions`, { timeout: VIDEO_TIMEOUT });
    newPermissions = data;
  } catch (e) {
    console.error('Error getting video permissions');
  }
  dispatch(setPermissions(newPermissions));
  return newPermissions;
};

export const userHasRemainingQuota = () => async (dispatch) => {
  try {
    const { data: { hasRemainingQuota } } = await axios.get(`/api/audits/quota?assetType=${ASSET_TYPES.video.type}`, { timeout: VIDEO_TIMEOUT });
    dispatch(setUserHasRemainingQuota(hasRemainingQuota));
  } catch (e) {
    console.error('Error getting quota information for video');
    dispatch(setUserHasRemainingQuota(true));
  }
};

export const videoDownloaded = (id, url) => async (dispatch) => {
  try {
    const extension = url.split('?').shift().split('.').pop();
    await axios.post('/api/audits/download', {
      assetId: id,
      assetType: 'video',
      assetVersion: 'paid',
      extension,
    }, {
      timeout: VIDEO_TIMEOUT,
    });

    dispatch(userHasRemainingQuota());
  } catch (err) {
    const { message } = getAxiosError(err);
    console.error(`video download audit failed; video: ${id}; message: ${JSON.stringify(message)}`);
  } finally {
    dispatch(setVideoDownloading(false));
  }
};

// TODO - remove this when wazee videos can be used for news-article videos (requires some Mars work)
export const downloadLegacyVideo = (id, url) => async (dispatch) => {
  if (!(id && url)) return;

  try {
    const { data: { hasRemainingQuota } } = await axios.get(`/api/audits/quota?assetType=${ASSET_TYPES.video.type}`, { timeout: VIDEO_TIMEOUT });
    if (!hasRemainingQuota) {
      dispatch(showQuotaExceededModal());
    } else {
      // TODO make sure the extension is part of the url for video
      const extension = url.split('.').pop();
      saveAs(url, `${id}.${extension}`);
      try {
        await axios.post('/api/audits/download', {
          assetId: id,
          assetType: ASSET_TYPES.video.type,
          assetVersion: 'paid',
          extension,
        }, {
          timeout: VIDEO_TIMEOUT,
        });
      } catch (err) {
        const { message } = getAxiosError(err);
        console.error(`video download audit failed; video: ${id}; message: ${JSON.stringify(message)}`);
      }
    }
  } catch (err) {
    handleError(dispatch, err);
  }
};
